import { type ReactNode, useId } from 'react'
import { cn } from '@/shared/lib'
import type { CustomerVisibilityBlock } from '../model/schema'

interface CustomerVisibilityFieldProps {
  checked: boolean
  onChange: (visible: boolean) => void
  // Имя компании-клиента: подпись обязана называть, кто именно увидит встречу.
  companyName?: string | undefined
  // Инвариант, из-за которого переключать нельзя (бэк ответил бы 422).
  block?: CustomerVisibilityBlock | undefined
  // Запрос в полёте — тумблер не должен щёлкать дважды.
  busy?: boolean
  // Аудит пометки (кто и когда открыл) — показывается под подписью.
  footnote?: ReactNode
}

// Почему нельзя — текстом рядом с заблокированным тумблером: упереться в 422,
// чтобы узнать правило, — не объяснение.
const blockReason: Record<CustomerVisibilityBlock, string> = {
  private: 'Приватную встречу клиенту открыть нельзя — сначала снимите «скрыть от посторонних».',
  'no-company': 'Сначала укажите компанию встречи — без неё непонятно, чьему клиенту её открывать.',
}

// Общее поле «Видно заказчику» для формы создания, формы правки и карточки:
// одна формулировка на все три поверхности. Само переключение — дело
// вызывающего (форма копит состояние, карточка шлёт мутацию).
export function CustomerVisibilityField({
  checked,
  onChange,
  companyName,
  block,
  busy = false,
  footnote,
}: CustomerVisibilityFieldProps) {
  const id = useId()
  const descriptionId = `${id}-description`
  const disabled = busy || block !== undefined

  return (
    <div className="flex items-start gap-3 rounded-control bg-bg-surface px-4 py-3">
      <input
        id={id}
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        aria-describedby={descriptionId}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={id} className={cn('text-sm', disabled && 'opacity-60')}>
        <span className="text-text-primary">Видно заказчику</span>
        <span id={descriptionId} className="block text-text-tertiary">
          {block
            ? blockReason[block]
            : `Клиент компании ${companyName ?? 'встречи'} увидит эту встречу, её запись и расшифровку.`}
        </span>
        {footnote && <span className="mt-1 block text-text-tertiary">{footnote}</span>}
      </label>
    </div>
  )
}
